import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentProps } from 'react';

import { clsxm } from '@vs-shop/utils';

/**
 * The standing page an account flow gets when it is the only thing on screen:
 *
 *   <AuthPanel variant={AuthPanelVariant.PLAIN}>
 *     <AuthPanelColumn>
 *       <AuthPanelHeader>
 *         <Heading level={1}>Změna hesla</Heading>
 *       </AuthPanelHeader>
 *       <AuthPanelCard>
 *         <ResetPasswordForm … />
 *       </AuthPanelCard>
 *     </AuthPanelColumn>
 *   </AuthPanel>
 *
 * Signing in, registering, resetting a password and verifying an address are
 * four flows over four different forms, and one page around them: a band the
 * full width of the viewport, a narrow column centred in it, and — where a flow
 * puts its form on a surface of its own — a card.
 *
 * **Which parts a flow has is its own JSX.** A registration page carries a
 * notice above the column and no header; a verification page carries a header
 * and no card. Nothing here takes a prop deciding that, and a part a flow omits
 * simply leaves its place empty.
 *
 * **Nothing about accounts crosses into this package.** No form, no schema, no
 * modal atom, no route and no copy: what stands in the column arrives as
 * children from tenant JSX, which is also where validation and submission live.
 *
 * The heading is the page's: a panel is furniture, so it renders no heading
 * element of its own and a page composes `@vs-shop/ui/heading` into the header
 * at the level its outline calls for.
 *
 * Server-first: every part is a plain component, and whatever interactive form
 * a flow puts in the column is the tenant's own client leaf.
 */

/**
 * The ground the panel stands on.
 *
 * Two arms because the two shapes of this page want different grounds: a flow
 * whose form sits straight on the page needs the page's own, and one whose form
 * sits on a card needs a shade off it, or the card would not read as raised.
 */
export const authPanelVariants = cva(
  'flex w-full flex-col items-center justify-center gap-4 pt-12 pb-24',
  {
    variants: {
      variant: {
        /** The page's own ground, for a flow whose form stands directly on it. */
        plain: 'bg-background',
        /** A shade off it, so a card standing on top reads as raised. */
        shaded: 'bg-background-intense',
      },
    },
  }
);

type AuthPanelVariantName = NonNullable<VariantProps<typeof authPanelVariants>['variant']>;

export type AuthPanelVariant = AuthPanelVariantName;

/** `satisfies` binds these to the recipe above, so the two cannot drift. */
export const AuthPanelVariant = {
  /** The page's own ground. */
  PLAIN: 'plain',
  /** A shade off it, under a card. */
  SHADED: 'shaded',
} as const satisfies Record<string, AuthPanelVariantName>;

/** Required and closed — not `VariantProps`, which makes it optional. */
export type AuthPanelVariantProps = { readonly variant: AuthPanelVariant };

export type AuthPanelProps = ComponentProps<'div'> & AuthPanelVariantProps;

/** The band the flow stands in. */
export const AuthPanel = ({ className, variant, ...props }: AuthPanelProps) => (
  <div
    data-slot="auth-panel"
    className={clsxm(authPanelVariants({ variant }), className)}
    {...props}
  />
);

export type AuthPanelColumnProps = ComponentProps<'div'>;

/**
 * The panel's measure.
 *
 * A form is read top to bottom and its fields are one per line, so the measure
 * narrows rather than widens as the viewport grows — a field the width of a
 * desktop is harder to scan, not easier.
 */
export const AuthPanelColumn = ({ className, ...props }: AuthPanelColumnProps) => (
  <div
    data-slot="auth-panel-column"
    className={clsxm('lap:w-3/5 des:w-2/5 w-4/5 max-w-[32rem]', className)}
    {...props}
  />
);

export type AuthPanelHeaderProps = ComponentProps<'div'>;

/**
 * What the flow is called, and the space under it.
 *
 * Ranged left on a phone and centred once there is a column's worth of room
 * around it — the same title reads as a label at the top of a narrow screen and
 * as a page's own name in the middle of a wide one.
 */
export const AuthPanelHeader = ({ className, ...props }: AuthPanelHeaderProps) => (
  <div
    data-slot="auth-panel-header"
    className={clsxm('lap:text-center mb-6 text-left', className)}
    {...props}
  />
);

export type AuthPanelCardProps = ComponentProps<'div'>;

/** The surface a flow puts its form on, where it wants one. */
export const AuthPanelCard = ({ className, ...props }: AuthPanelCardProps) => (
  <div
    data-slot="auth-panel-card"
    className={clsxm('bg-background ring-foreground/5 rounded-lg p-6 shadow-sm ring', className)}
    {...props}
  />
);
